var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/BuildifyLitElement.ts
import { A } from "@adornis/base/env-info.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { ModeConsumer, SizeConsumer } from "@adornis/buildify/client/globals/consumer.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { ComponentSizeController } from "@adornis/chemistry/controllers/component-size-controller.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-icon";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { filter, map } from "rxjs";
import "../db/enums.js";
var convertDataBySize = (data, size) => {
  const filteredConfigsBySize = data.configs.filter((config) => config.size === size);
  const resolvedClass = baseqlMetaData.entities.resolveNode(data._class).type();
  if (!resolvedClass)
    throw new Error(`class with classname ${data._class} could not be resolved`);
  const convertedContent = new resolvedClass({ ...data.toObject(), _id: A.getGloballyUniqueID() });
  for (const config of filteredConfigsBySize) {
    let changeValue = convertedContent;
    for (const [index, key] of config.keyArray.entries()) {
      let parsedKey = key;
      const keyNumberParsed = Number.parseInt(key);
      if (!Number.isNaN(keyNumberParsed)) {
        parsedKey = keyNumberParsed;
      }
      if (index === config.keyArray.length - 1) {
        changeValue[parsedKey] = config.value;
      } else {
        changeValue = changeValue[parsedKey];
      }
    }
  }
  Object.freeze(convertedContent);
  return convertedContent;
};
var BuildifyLitElement = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.data = new RXController(this, void 0);
    this.hasNoContent = false;
    this._constructedData = new RXController(
      this,
      this.data.observable.pipe(
        filter(Boolean),
        map((dataString) => {
          const parsedString = JSON.parse(dataString);
          if (typeof parsedString !== "object")
            return void 0;
          const constructed = constructValue(parsedString);
          return constructed;
        })
      )
    );
    this._data = new RXController(this, void 0);
    this._isHovering = false;
    this._componentSize = new ComponentSizeController(this, { triggerUpdates: false });
    this._consumedSize = SizeConsumer(this);
    this._consumedMode = ModeConsumer(this);
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    if (this._constructedData.value && (this._constructedData.value !== this._data.value || this._lastSize !== this._consumedSize.value)) {
      this._data.next(convertDataBySize(this._constructedData.value, this._consumedSize.value));
      this._lastSize = this._consumedSize.value;
    }
  }
  _isPrint() {
    return window.matchMedia && window.matchMedia("print").matches;
  }
  connectedCallback() {
    super.connectedCallback();
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "attributes" || mutation.attributeName !== "class")
          continue;
        this.requestUpdate();
        break;
      }
    });
    observer.observe(this, {
      attributes: true
      // Beobachtet Attributänderungen
    });
    this.addEventListener("mouseenter", () => {
      this._isHovering = true;
    });
    this.addEventListener("mouseleave", () => {
      this._isHovering = false;
    });
  }
  render() {
    const content = this._data.value;
    const mode = this._isPrint() ? "print" : this._consumedMode.value ?? "view";
    return html`
      <!-- overlay -->
      ${this._getOverlay(mode)}
      <!-- content -->
      ${xComponents(this.content({ mode, content }))}
    `;
  }
  _getOverlay(mode) {
    if (mode === "view" || mode === "print")
      return nothing;
    const isFocused = this.classList.contains("ProseMirror-focused") || this.classList.contains("ProseMirror-selectednode");
    if (!this._isHovering && !isFocused)
      return nothing;
    if (isFocused && this.hasNoContent) {
      return html`
        <div
          ${css({
        position: "absolute",
        left: "0",
        right: "0",
        top: "0",
        bottom: "0",
        zIndex: "100",
        background: "rgba(0,0,0,.75)",
        pointerEvents: "all",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      })}
        ></div>
      `;
    }
    if (isFocused && !this.hasNoContent)
      return nothing;
    return html`
      <div
        ${css({
      position: "absolute",
      left: "0",
      right: "0",
      top: "0",
      bottom: "0",
      border: "1px dashed #333",
      zIndex: "100",
      pointerEvents: this.hasNoContent ? "all" : "none",
      cursor: this.hasNoContent ? "pointer" : "",
      background: this.hasNoContent ? "rgba(0,0,0,.5)" : ""
    })}
      >
        ${this.hasNoContent ? nothing : html`
              <x-icon
                ${css({
      position: "absolute",
      right: "-2px",
      top: "-2px",
      pointerEvents: "all",
      background: "#333",
      fontSize: "16px",
      padding: "1px",
      color: "#fff",
      cursor: "pointer"
    })}
              >
                check_box_outline_blank
              </x-icon>
            `}
      </div>
    `;
  }
  content({ mode, content }) {
    return html``;
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          position: "relative",
          overflow: "hidden",
          display: "block",
          whiteSpace: "normal",
          boxSizing: "border-box"
        }
      }
    ];
  }
};
__decorateClass([
  property({ attribute: "data", type: String })
], BuildifyLitElement.prototype, "data", 2);
__decorateClass([
  property({ attribute: "no-content", type: Boolean })
], BuildifyLitElement.prototype, "hasNoContent", 2);
__decorateClass([
  state()
], BuildifyLitElement.prototype, "_constructedData", 2);
__decorateClass([
  state()
], BuildifyLitElement.prototype, "_data", 2);
__decorateClass([
  state()
], BuildifyLitElement.prototype, "_isHovering", 2);
export {
  BuildifyLitElement
};
